import React, { useState } from 'react';
import { NavLink as RouterLink, useLocation, matchPath } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const BRAND = '#682622';
const BRAND_DARK = '#4e1b18';
const BRAND_LIGHT = '#fdf5f4';
const BRAND_MID = '#fce8e7';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
  },
  // Top-level items (depth === 0)
  button: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#546e7a',
    borderRadius: 8,
    padding: '9px 12px',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: BRAND_LIGHT,
      color: BRAND,
      '& $icon': { color: BRAND },
    },
  },
  buttonActive: {
    backgroundColor: BRAND,
    color: '#fff',
    fontWeight: 600,
    '& $icon': { color: '#fff' },
    '&:hover': {
      backgroundColor: BRAND_DARK,
      color: '#fff',
      '& $icon': { color: '#fff' },
    },
  },
  // Child items (depth > 0)
  childButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: 400,
    fontSize: '0.825rem',
    color: '#78909c',
    borderRadius: 6,
    padding: '7px 12px',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: BRAND_LIGHT,
      color: BRAND,
      '& $childDot': { opacity: 1, backgroundColor: BRAND },
    },
  },
  childButtonActive: {
    color: BRAND,
    fontWeight: 600,
    backgroundColor: BRAND_LIGHT,
    '& $childDot': { opacity: 1, backgroundColor: BRAND },
    '&:hover': {
      backgroundColor: BRAND_MID,
      color: BRAND_DARK,
    },
  },
  icon: {
    color: '#90a4ae',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1.5),
    transition: 'color 0.15s ease',
    '& svg': { fontSize: '1.15rem' },
  },
  iconSmall: {
    '& svg': { fontSize: '0.95rem' },
    color: '#b0bec5',
  },
  expandIcon: {
    marginLeft: 'auto',
    fontSize: '1rem',
    opacity: 0.5,
    flexShrink: 0,
    transition: 'transform 0.2s ease, opacity 0.15s ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
    opacity: 0.8,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  childDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: '#b0bec5',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
    opacity: 0.6,
    transition: 'opacity 0.15s ease, background-color 0.15s ease',
  },
  collapseList: {
    paddingTop: 2,
    paddingBottom: 4,
  },
}));

const NavigationListItem = ({
  title,
  href,
  depth = 0,
  children,
  icon: Icon,
  className,
  open: openProp = false,
  label: Label,
  onLinkClick,
  ...rest
}) => {
  const classes = useStyles();
  const location = useLocation();
  const [open, setOpen] = useState(openProp);

  const isActive = href
    ? Boolean(matchPath({ path: href, end: depth > 0 }, location.pathname))
    : false;

  const handleToggle = () => setOpen(prev => !prev);
  const handleItemClick = () => { if (onLinkClick) onLinkClick(); };

  const paddingLeft = 12 + depth * 16;

  if (children) {
    const isChildGroup = depth > 0;
    return (
      <ListItem {...rest} className={clsx(classes.item, className)} disableGutters>
        <Button
          className={isChildGroup ? classes.childButton : classes.button}
          onClick={handleToggle}
          style={{ paddingLeft }}
        >
          {Icon && (
            <span className={clsx(classes.icon, isChildGroup && classes.iconSmall)}>
              <Icon />
            </span>
          )}
          {title}
          <ExpandMoreIcon
            className={clsx(classes.expandIcon, open && classes.expandIconOpen)}
          />
        </Button>
        <Collapse className={classes.collapseList} in={open}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  const isChild = depth > 0;

  return (
    <ListItem {...rest} className={clsx(classes.itemLeaf, className)} disableGutters>
      <Button
        className={clsx(
          isChild ? classes.childButton : classes.button,
          isActive && (isChild ? classes.childButtonActive : classes.buttonActive),
        )}
        component={RouterLink}
        to={href}
        style={{ paddingLeft }}
        onClick={handleItemClick}
      >
        {!isChild && Icon && <span className={classes.icon}><Icon /></span>}
        {isChild && <span className={classes.childDot} />}
        {title}
        {Label && <span className={classes.label}><Label /></span>}
      </Button>
    </ListItem>
  );
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  label: PropTypes.element,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  onLinkClick: PropTypes.func,
};

export default NavigationListItem;
